<template>
  <div>
    <!-- log-tool-bar -->
    <el-row class="mr-b10" type="flex" justify="space-between">
      <w-log-search-field :field-options="fieldOptions" :search-keywords="searchKeywords"
        @add-search-tag="addSearchTag" @delete-hist-by-index="onDeleteByIndex" @delete-all-history="onDeleteAllHistory"/>
      <div align="right">
        <el-tooltip placement="left" content="화면 분할">
          <el-button class="customised-icon divide" size="small" v-if="isFirst" @click="splitTable"></el-button>
        </el-tooltip>
        <el-button size="small" type="primary" @click="onToggleRealtime">
          <i v-show="!realtimeButtonActive" class="fas fa-play-circle text-large mr-r4" />
          <i v-show="realtimeButtonActive" class="fas fa-pause-circle text-large mr-r4" /> 실시간 조회
        </el-button>
      </div>
    </el-row>
    <!-- /log-tool-bar -->
    <!-- search-tags -->
    <hr class="mr-b10" />
    <el-row class="mr-b10 vertical-center-box">
      <span>
        <el-tag class="label mr-r10">태그</el-tag>
      </span>
      <span v-if="!isEmpty(searchTags)">
        <el-tag class="mr-r4" v-for="(tag, index) in searchTags" :key="`${tag.field}_${tag.value}`" :type="tag.regex === 'Y' ? 'success' : null" closable @close="deleteSearchTag(index)">
          {{tag.label}}
        </el-tag>
      </span>
    </el-row>
    <!-- /search-tags -->
    <!-- log-data-grid -->
    <el-table-wrapper v-infinite-scroll="loadScrollData" border ref="logTable" :fit="true" :total="total" :column-default="columnDefault" :columns="columns" :data="logDatas" :pagination="false"
      highlight-current-row @cell-click="getSelection">
      <template slot-scope="scope" slot="log-detail">
        <span class="c-pointer" @click="openDetailPopup(scope.row)">
          <i class="far fa-search-plus"/>
        </span>
      </template>
    </el-table-wrapper>
    <!-- /log-data-grid -->
    <w-log-detail-popup ref="logDetailPopup" />
    <el-popover ref="miniPopup" v-model="showAddTag" @blur="closeTagPopup" align="center"
    :style="showAddTag ? { top: `${tagPopupPosition.top}px`, left: `${tagPopupPosition.left}px`, position: 'absolute',
    height: '50px', width: '300px' } : { display: 'none' }">
      <el-row type="flex" justify="space-between">
        <div class="vertical-center-box mr-r8" style="word-break: 'break-all'">{{selectedText.value}}</div>
        <div class="vertical-center-box" align="right">
          <el-button type="primary" @click="onAddSearchButton" class="mr-r4">검색</el-button>
          <el-button @click="closeTagPopup">취소</el-button>
        </div>
      </el-row>
      <span v-show="checkDuplicate" style="color: #E02323; margin-top: 10px; font-size: 0.75rem;">이미 태그에 추가된 검색어입니다.</span>
    </el-popover>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isEmpty, isNil, forEach } from 'lodash';

import LogAPI from '@/api/log/integrated-log';
import { columnDefault } from '@/constants/table';
import { EMPTY_TEXT } from '@/utils/string';
import { getDateToString, getIntervalDatetimeParams } from '@/utils/date';
import msgUtils from '@/utils/message';

import WLogSearchField from '@/views/log-analysis/components/LogSearchField';
import WLogDetailPopup from '@/views/log-analysis/popup/LogDetailPopup';

const CNT_PER_PAGE = 50;

export default {
  name: 'WRealtimeLogSearch',
  components: {
    WLogDetailPopup,
    WLogSearchField,
  },
  props: {
    isFirst: Boolean,
    searchKeywords: Array,
    active: {
      type: Boolean,
      default: false,
    },
    fieldOptions: Array,
  },
  data() {
    return {
      logDatas: [],
      columnDefault,
      columns: [],
      fieldsByIndex: [],
      showAddTag: false,
      checkDuplicate: false,
      isSplit: false,
      tagPopupPosition: { // 검색어 추가 팝업의 위치
        top: 0,
        left: 0,
      },
      isRealtime: true,
      selectedText: {},
      searchTags: [],
      total: null,
      start: 0,
      limit: 50,
      realtimeButtonActive: true,
      realtimeLogTask: this.isFirst ? 'realtimeLogOriginTask' : 'realtimeLogSecondTask',
      scrolling: false,
    };
  },
  computed: mapGetters(['selectedNode', 'analysisDateParams']),
  watch: {
    active: {
      handler(to) {
        if (to) {
          this.searchTags = [];
          this.realtimeButtonActive = true;
          this.limit = 50;
          this.loadData();
          this.createRealtimeLogTask(this.realtimeButtonActive);
        } else {
          this.isSplit = false;
          this.removeRealtimeLogTask();
        }
      },
    },
    selectedNode: {
      immediate: true,
      handler(value) {
        this.searchTags = [];
        if (!isEmpty(value) && this.active) {
          this.$nextTick(() => {
            this.limit = CNT_PER_PAGE;
            this.loadData();
          });
        }
      },
    },
    /**
     * 선택한 트리 노드에 따라 필드가 변경되었을 경우 로그 조회 그리드에 동적 컬럼 생성
     * @type {Object}
     */
    fieldOptions: {
      immediate: true,
      deep: true,
      handler(value) {
        this.searchTags = [];
        this.columns = [];
        this.$nextTick(() => {
          if (!isEmpty(value)) {
            forEach(value, (item) => {
              this.columns.push({
                label: item.fieldName,
                prop: item.fieldId,
                width: this.getColumnWidth(item.fieldId),
                minWidth: 100,
                formatter: item.fieldId === 'timestamp' ? this.getDateFormatter : this.getStringFormatter,
                showOverflowTooltip: item.fieldId !== 'originMessage',
                renderHeader: this.appendHeaderTooltip,
              });
            });
            this.columns.push({
              label: '상세보기',
              width: 70,
              scopedSlot: 'log-detail',
              align: 'center',
              // fixed: 'right',
            });
          }
        });
      },
    },
    searchTags: {
      deep: true,
      handler(value) {
        this.$emit('change-search-tags', value);
      },
    },
  },
  mounted() {
    this.limit = CNT_PER_PAGE;
    this.createRealtimeLogTask(this.realtimeButtonActive);
  },
  beforeDestroy() {
    this.removeRealtimeLogTask();
  },
  methods: {
    isEmpty,
    createRealtimeLogTask(andStart) {
      this.$taskManager.createTask(this.realtimeLogTask, 10, this.loadData);
      if (andStart) {
        this.$taskManager.startTask(this.realtimeLogTask);
      }
    },
    removeRealtimeLogTask() {
      if (this.$taskManager.existTask(this.realtimeLogTask)) {
        this.$taskManager.removeTask(this.realtimeLogTask);
      }
    },
    /**
     * 로그를 필터링할 검색 조건을 parameter로 전송해 로그를 조회한다. startDateTime/endDateTime은 GMT > KST로 변환(+09:00)해 전송한다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadData() {
      let params;
      if (this.selectedNode.type === 'dataindex') {
        params = { type: this.selectedNode.type, dataindexId: this.selectedNode.dataindexId };
      } else if (this.selectedNode.type === 'filter') {
        params = { type: this.selectedNode.type, customFilterId: this.selectedNode.customFilterId };
      } else if (this.selectedNode.type === 'group') {
        params = { type: 'group', filterGroupId: this.selectedNode.filterGroupId };
      } else {
        params = { type: 'root' };
      }
      const queryList = this.setQueryList();
      params = {
        ...params,
        queryList,
        startDateTime: this.analysisDateParams.startDatetime,
        endDateTime: this.analysisDateParams.endDatetime,
        start: this.start,
        limit: this.limit,
      };
      // if (!isEmpty(this.logDatas)) {
      //   params.searchAfter = this.logDatas[0].searchAfter;
      //   params.isRealTimeSearch = true;
      // }
      this.scrolling = true;
      LogAPI.getLogDatas(params).then((response) => {
        if (response.data.success) {
          this.logDatas = response.data.data;
          // if (!isEmpty(this.logDatas) && !this.initFlag) {
          //   this.logDatas = result.reverse().concat(this.logDatas);
          //   if (this.logDatas.length > 1000) {
          //     this.logDatas = this.logDatas.slice(0, 1000);
          //   }
          // } else {
          //   this.logDatas = result;
          // }
          this.total = response.data.total;
        }
      }).catch((e) => {
        this.$message(msgUtils.getResultMsgData(msgUtils.action.VIEW, '로그', false));
        throw new Error(e);
      }).finally(() => {
        this.scrolling = false;
      });
    },
    /**
     * 태그 검색어 및 헤더 검색어를 queryList 데이터로 가공
     * @author Minyeong
     * @param  {[type]} queryList [description]
     */
    setQueryList() {
      const queryList = [];
      forEach(this.searchTags, (item) => {
        queryList.push({ field: item.field, value: item.value, operator: 'AND', regex: item.regex });
      });
      return queryList;
    },
    loadScrollData() {
      if (!this.scrolling && !isEmpty(this.logDatas)) {
        this.limit += CNT_PER_PAGE;
        this.loadData();
      }
    },
    /**
     * 그리드 선택 검색 시 검색어를 태그 검색어에 추가한다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    onAddSearchButton() {
      this.addSearchTag(this.selectedText);
    },
    /**
     * 태그 검색어를 추가하고 쿠키에 검색 이력을 남긴다.
     * @author Minyeong
     * @param  {[type]} selectedText [description]
     */
    addSearchTag(selectedText) {
      this.limit = CNT_PER_PAGE;
      this.searchTags.push(selectedText);
      this.loadData();
      this.$emit('set-search-history', selectedText);
      this.closeTagPopup();
    },
    /**
     * Grid Selection Search(그리드 선택 검색) 로직 : 선택한 단어를 간편검색 팝오버에 띄운다.
     * @author Minyeong
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} cell   [description]
     * @param  {[type]} event  [description]
     * @return {[type]}        [description]
     */
    getSelection(row, column, cell, event) {
      event.stopPropagation();
      // 수집시각은 상단 Datepicker에서 설정할 수 있으므로 검색 제한
      if (column.property === 'timestamp') {
        return;
      }
      this.checkDuplicate = false; // 이미 검색어로 태그에 추가되었는지 확인하는 Flag
      const selected = window.getSelection(); // 사용자가 선택한 영역 정보
      const selectedText = selected.toString(); // 영역 정보로부터 사용자가 선택한 텍스트 읽어오기
      if (selectedText && selected.anchorNode === selected.extentNode) {
        // 선택된 텍스트가 있을 때 : 태그 추가용 팝업 ON
        // top, left : 사용자가 선택한 영역의 좌표 위치. 태그 검색 팝업의 위치를 잡아주기 위해 설정
        const top = selected.getRangeAt(0).getBoundingClientRect().top;
        const left = selected.getRangeAt(0).getBoundingClientRect().left;
        this.tagPopupPosition.top = top - 170;
        this.tagPopupPosition.left = left - 260;

        // 태그 검색 팝업 활성화
        this.showAddTag = true;

        // 태그에 노출시킬 라벨(컬럼명 : 값)과 컬럼속성명, 값을 저장
        this.selectedText.label = `${column.label} : ${selectedText}`;
        this.selectedText.field = column.property;
        this.selectedText.value = selectedText;
        this.selectedText.regex = 'N';
      } else {
        // 없을 때에는 팝업 띄우지 않음
        this.closeTagPopup();
      }
    },
    /**
     * Grid Selection Search 팝오버를 닫는다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    closeTagPopup() {
      this.selectedText = {
        field: this.fieldOptions[0].fieldId,
      }; // 선택된 텍스트 초기화
      this.showAddTag = false;
    },
    /**
     * 모든 검색 이력을 삭제한다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    onDeleteAllHistory() {
      this.$emit('delete-all-history');
    },
    /**
     * 단일 검색 이력을 삭제한다.
     * @author Minyeong
     * @param  {[type]} index [삭제할 검색 이력의 인덱스]
     * @return {[type]}       [description]
     */
    onDeleteByIndex(index) {
      this.$emit('delete-by-index', index);
    },
    /**
     * 검색 태그를 삭제한다.
     * @author Minyeong
     * @param  {[type]} index [삭제할 검색 태그의 인덱스]
     * @return {[type]}       [description]
     */
    deleteSearchTag(index) {
      this.limit = CNT_PER_PAGE;
      this.searchTags.splice(index, 1);
      this.loadData();
    },
    /**
     * 로그 조회 그리드를 분할한다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    splitTable() {
      this.$emit('split-table');
      this.isSplit = !this.isSplit;
      if (this.isSplit) {
        this.logDatas = [];
        this.loadData();
      }
    },
    /**
     * 실시간 버튼을 클릭한다. (실시간 조회 ON/OFF)
     * @author Minyeong
     * @param  {[type]} event [description]
     * @return {[type]}       [description]
     */
    onToggleRealtime(event) {
      event.stopPropagation();
      this.realtimeButtonActive = !this.realtimeButtonActive;
      if (this.realtimeButtonActive) {
        // 실시간 로그 ON
        this.$taskManager.startTask(this.realtimeLogTask);
      } else {
        // 실시간 로그 OFF
        this.$taskManager.stopTask(this.realtimeLogTask);
      }
    },
    /**
     * 단일 로그 상세 정보 팝업을 활성화한다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    openDetailPopup(log) {
      this.$refs.logDetailPopup.open(log);
    },
    /**
     * 그리드 수집시간을 표현하기 위한 포매터. 실제 조회되는 timestamp는 GMT 기준이므로 KST로 변경(-09:00)해준다.
     * @author Minyeong
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(getIntervalDatetimeParams(value, 'hour', 9), 'YYYY-MM-DD HH:mm:ss');
      }
      return EMPTY_TEXT;
    },
    getStringFormatter(row, column, value) {
      if (!isNil(value)) {
        return value;
      }
      return EMPTY_TEXT;
    },
    /**
     * 동적 컬럼의 Width를 설정한다.
     * @author Minyeong
     * @param  {[type]} fieldId [description]
     * @return {[type]}         [description]
     */
    getColumnWidth(fieldId) {
      if (fieldId === 'timestamp') {
        return 150;
      } else if (fieldId === 'originMessage') {
        return 700;
      }
      return null;
    },
    /**
     * Table Header Overflow Tooltip : 컬럼의 가로길이보다 텍스트의 가로길이가 길어 ...으로 축약되는 경우 헤더에도 툴팁을 달아준다.
     * @author Minyeong
     * @param  {[type]} h    [description]
     * @param  {[type]} data [description]
     * @return {[type]}      [description]
     */
    appendHeaderTooltip(h, data) {
      const text = document.createElement('canvas');
      const ctx = text.getContext('2d');
      // el-table에서 사용하고 있는 폰트 사이즈 및 폰트 종류
      ctx.font = '0.75rem NunitoSans';
      const textWidth = ctx.measureText(data.column.label).width;
      // 80 : 컬럼의 minWidth인 100을 기준으로 텍스트가 차지할 수 있는 최대 영역의 width
      const maxTextWidth = 80;
      return h('el-tooltip', { attrs: { content: data.column.label, effect: 'dark', placement: 'top', trigger: 'hover', disabled: textWidth < maxTextWidth } }, [
        data.column.label,
        h(
          'span',
          {
            attrs: {
              slot: 'reference',
            },
          },
          [data.column.label],
        ),
      ]);
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/log-analysis/RealtimeSearch/components/RealtimeLogSearch.vue